import React, { useMemo } from "react";

import { useSelector } from "react-redux";
import { Field, FormSection } from "redux-form";
import {
	ReduxControls,
	ErrorBanner
} from "rhombus-react";

import AssetSelect from "social/AssetSelect";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { TWITTER_CONVERSATION_CONTROL_OPTIONS } from "social/constants";
import { prettifyMessages } from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { TWITTER_VIDEO_CONSTRAINTS } from "social/validators/twitter";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";

const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_TWEET,
	MAX_LENGTH_LANDING_PAGE,
	MAX_VIDEO_MB,
	DURATION,
	VIDEO_ACCEPT,
} = TWITTER_VIDEO_CONSTRAINTS;

const TWITTER_VIDEO_FIELDS = {
	video: {
		name: "video",
		label: "Video",
		assetType: ASSET_TYPES.VIDEO,
		requirements: [
			{
				name: "File Size",
				description: `${MAX_VIDEO_MB}MB Max`,
			},
			{
				name: "File Type",
				description: VIDEO_ACCEPT,
			},
			{
				name: "Duration",
				description: `${DURATION.max}s Max`,
			},
			{
				name: "Dimensions",
				description: "Exactly 16:9 or 1:1",
			},
		],
		component: AssetSelect,
		accept: VIDEO_ACCEPT,
		tip: {
			direction: "top",
			description: "Videos will loop if the video length is under 60 seconds",
		},
		required: true,
		className: "full-width",
	},
	name: {
		name: "name",
		label: "Creative Name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
		className: "full-width",
	},
	headline: {
		name: "headline",
		label: "Headline",
		placeholder: "Enter a short headline",
		component: ReduxControls.Input,
		tip: {
			direction: "right",
			description: "May get truncated after 50 characters",
		},
		charMax: MAX_LENGTH_HEADLINE,
		showCharCount: true,
		required: true,
		className: "full-width",
	},
	tweet: {
		name: "tweet",
		label: "Tweet",
		placeholder: "Enter the tweet copy",
		component: ReduxControls.Input,
		required: true,
		charMax: MAX_LENGTH_TWEET,
		showCharCount: true,
		className: "full-width",
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "https://www.example.com",
		component: ReduxControls.Input,
		required: true,
		className: "full-width",
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
	},
	conversationControl: {
		name: "conversationControl",
		label: "Conversation Control",
		component: ReduxControls.Select,
		options: TWITTER_CONVERSATION_CONTROL_OPTIONS,
		className: "half-width",
		isClearable: false,
		normalize: option => option.value,
		format: value => ({ label: value, value }),
		menuPlacement: "top",
	},
};


const adType = SOCIAL_AD_TYPES.TWITTER_VIDEO;

export default function TwitterVideo(props){
	const { navigationActions, libraryId, isViewOnly, showPristineErrors } = props;

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, TWITTER_VIDEO_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(TWITTER_VIDEO_FIELDS).map((fieldProps) => {
		const extraProps = {};

		if (fieldProps.name === "video") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
		}

		extraProps.disabled = isViewOnly;

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

TwitterVideo.displayName = "TwitterVideoForm";
